import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Star, 
  Trophy, 
  Users, 
  Flame, 
  CheckCircle2, 
  ArrowRight,
  Medal
} from 'lucide-react';

// Alnoor Islamic Center Logo
const LOGO_URL = '/AIC-Logo.png';

export default function Home() {
  const { user, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  const features = [
    {
      icon: CheckCircle2,
      title: 'Daily Islamic Challenges',
      description: 'Complete fun daily tasks that strengthen faith and build good habits.'
    },
    {
      icon: Star,
      title: 'Earn Points & Rewards',
      description: 'Gain points for every completed task and unlock special achievements.'
    },
    {
      icon: Flame,
      title: 'Build Streaks',
      description: 'Stay consistent and watch your streak grow day by day.'
    },
    {
      icon: Trophy,
      title: 'Achievements & Badges',
      description: 'Collect beautiful badges and show off your accomplishments.'
    },
    {
      icon: Medal,
      title: 'Leaderboard Rankings',
      description: 'Compete with other young Muslims and climb the leaderboard.'
    },
    {
      icon: Users,
      title: 'Family Friendly',
      description: 'Parents can manage multiple children and track their progress.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#FFFBEB]">
      {/* Hero Section */}
      <header className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 islamic-pattern opacity-50" />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#08513F]/5 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Navigation */}
          <nav className="flex items-center justify-between py-6">
            <div className="flex items-center gap-3">
              <img 
                src={LOGO_URL} 
                alt="Alnoor Islamic Center" 
                className="h-12 w-12 object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div>
                <h1 className="text-xl font-bold text-[#08513F] font-outfit">AIC Super Muslims</h1>
                <p className="text-xs text-[#D4AF37] font-semibold">Challenge</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {user ? (
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="bg-[#08513F] hover:bg-[#064031] text-white rounded-full px-6"
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Dashboard
                </Button>
              ) : (
                <>
                  <Link to="/login" className="text-[#264653] hover:text-[#08513F] font-medium">
                    Sign In
                  </Link>
                  <Button
                    onClick={() => navigate('/register')}
                    className="bg-[#E76F51] hover:bg-[#D65A3C] text-white rounded-full px-6"
                    data-testid="get-started-btn"
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </nav>

          {/* Hero Content */}
          <div className="py-20 lg:py-32 text-center">
            <div className="animate-slide-up">
              <span className="inline-block px-4 py-2 rounded-full bg-[#D4AF37]/20 text-[#B5952F] font-semibold text-sm mb-6">
                For Ages 8-15
              </span>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#264653] font-outfit mb-6 leading-tight">
                Become a{' '}
                <span className="text-[#08513F]">Super Muslim</span>
                <br />
                One Challenge at a Time
              </h1>
              
              <p className="text-lg sm:text-xl text-[#6B7280] max-w-2xl mx-auto mb-10">
                A fun and engaging way for young Muslims to complete daily Islamic challenges, 
                earn points, and grow closer to Allah.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  onClick={handleGetStarted}
                  className="bg-[#E76F51] hover:bg-[#D65A3C] text-white text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
                  data-testid="hero-get-started-btn"
                >
                  Start the Challenge
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                
                {!user && (
                  <Button
                    onClick={loginWithGoogle}
                    variant="outline"
                    className="border-2 border-[#08513F] text-[#08513F] hover:bg-[#08513F] hover:text-white text-lg px-8 py-6 rounded-full transition-all"
                    data-testid="google-signin-btn"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Sign in with Google
                  </Button>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="mt-16 grid grid-cols-3 gap-8 max-w-lg mx-auto animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <div className="text-center">
                <p className="text-3xl font-bold text-[#08513F] font-outfit">100+</p>
                <p className="text-sm text-[#6B7280]">Challenges</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-[#D4AF37] font-outfit">50+</p>
                <p className="text-sm text-[#6B7280]">Achievements</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-[#E76F51] font-outfit">∞</p>
                <p className="text-sm text-[#6B7280]">Fun</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#264653] font-outfit mb-4">
              Why Join the Challenge?
            </h2>
            <p className="text-lg text-[#6B7280] max-w-2xl mx-auto">
              Make learning Islam fun and rewarding for your children
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-[#FFFBEB] p-8 rounded-3xl border border-[#D4AF37]/20 card-hover animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-14 h-14 rounded-2xl bg-[#08513F] flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#264653] font-outfit mb-3">
                  {feature.title}
                </h3>
                <p className="text-[#6B7280]">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-islamic-green relative overflow-hidden">
        <div className="absolute inset-0 islamic-pattern opacity-10" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white font-outfit mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
            Join hundreds of young Muslims at Alnoor Islamic Center in the Super Muslims Challenge. 
            Begin your path to becoming a better Muslim today!
          </p>
          <Button
            onClick={handleGetStarted}
            className="bg-[#D4AF37] hover:bg-[#B5952F] text-[#08513F] font-bold text-lg px-10 py-6 rounded-full shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
            data-testid="cta-get-started-btn"
          >
            Join the Challenge Now
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-white border-t border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src={LOGO_URL} 
                alt="Alnoor Islamic Center" 
                className="h-10 w-10 object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div>
                <p className="font-bold text-[#08513F] font-outfit">AIC Super Muslims Challenge</p>
                <p className="text-sm text-[#6B7280]">Alnoor Islamic Center</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="text-sm text-[#6B7280] hover:text-[#08513F] transition-colors">
                Privacy Policy
              </Link>
              <span className="text-[#D4AF37]">|</span>
              <p className="text-sm text-[#6B7280]">
                © {new Date().getFullYear()} Alnoor Islamic Center. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
